/**
 * Incident Store - Phase 13
 * 
 * Aggregates incidents from existing local stores.
 * Provides a unified interface for incident data access.
 */

import { listQueueItems, listSyncLog, getQueueStats } from '../offline/db';
import {
  normalizeQueueItemToIncident,
  normalizeSyncLogToIncident,
  isIncidentOpen,
  isIncidentCritical,
  INCIDENT_STATUS,
  SEVERITY,
  FOLLOWUP_STATUS
} from './incidentModel';

// Local cache for incidents (optional materialized view)
let incidentCache = [];
let lastCacheUpdate = 0;
const CACHE_TTL_MS = 5000; // 5 seconds

/**
 * Aggregate all incidents from available sources
 * @param {Object} options - { includeCompleted, includeSyncLogs, limit }
 * @returns {Promise<Array>}
 */
export async function aggregateAllIncidents(options = {}) {
  const {
    includeCompleted = true,
    includeSyncLogs = false,
    limit = 1000
  } = options;
  
  const incidents = [];
  
  try {
    // 1. Get queue items and normalize to incidents
    const queueItems = await listQueueItems();
    
    for (const item of queueItems) {
      // Skip completed if not requested
      if (!includeCompleted && item.status === 'completed') {
        continue;
      }
      
      const incident = normalizeQueueItemToIncident(item);
      incidents.push(incident);
    }
    
    // 2. Optionally include sync logs
    if (includeSyncLogs) {
      const syncLogs = await listSyncLog(200);
      
      for (const log of syncLogs) {
        const incident = normalizeSyncLogToIncident(log);
        incidents.push(incident);
      }
    }
    
    // 3. Sort by occurredAt descending (newest first)
    incidents.sort((a, b) => b.occurredAt - a.occurredAt);
    
    // 4. Apply limit
    return incidents.slice(0, limit);
    
  } catch (error) {
    console.error('[IncidentStore] Failed to aggregate incidents:', error);
    return [];
  }
}

/**
 * Get cached incidents or refresh if stale
 * @returns {Promise<Array>}
 */
export async function getCachedIncidents() {
  const now = Date.now();
  
  if (now - lastCacheUpdate > CACHE_TTL_MS) {
    incidentCache = await aggregateAllIncidents();
    lastCacheUpdate = now;
  }
  
  return incidentCache;
}

/**
 * Invalidate incident cache
 */
export function invalidateIncidentCache() {
  lastCacheUpdate = 0;
  incidentCache = [];
}

/**
 * Get incident summary statistics
 * @returns {Promise<Object>}
 */
export async function getIncidentSummary() {
  const incidents = await getCachedIncidents();
  
  const summary = {
    total: incidents.length,
    open: 0,
    resolved: 0,
    failed: 0,
    conflict: 0,
    critical: 0,
    needsReview: 0,
    needsFollowup: 0,
    escalated: 0,
    
    // By severity
    bySeverity: {
      info: 0,
      warning: 0,
      error: 0,
      critical: 0
    },
    
    // By domain
    byDomain: {},
    
    // By source
    bySource: {},
    
    // By location
    byLocation: {},
    
    // Time-based
    last24h: 0,
    last7d: 0,
    
    // Unique entities
    uniqueLocations: new Set(),
    uniqueTerminals: new Set(),
    uniqueActors: new Set()
  };
  
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  
  for (const incident of incidents) {
    // Status counts
    if (isIncidentOpen(incident)) {
      summary.open++;
    } else {
      summary.resolved++;
    }
    
    if (incident.status === INCIDENT_STATUS.FAILED) summary.failed++;
    if (incident.status === INCIDENT_STATUS.CONFLICT) summary.conflict++;
    if (incident.status === INCIDENT_STATUS.MANUAL_REVIEW) summary.needsReview++;
    
    // Severity
    summary.bySeverity[incident.severity]++;
    if (isIncidentCritical(incident)) summary.critical++;
    
    // Follow-up status
    if (incident.followUpStatus === FOLLOWUP_STATUS.NEEDS_FOLLOWUP) summary.needsFollowup++;
    if (incident.followUpStatus === FOLLOWUP_STATUS.ESCALATED) summary.escalated++;
    
    // Domain
    summary.byDomain[incident.domain] = (summary.byDomain[incident.domain] || 0) + 1;
    
    // Source
    summary.bySource[incident.sourceType] = (summary.bySource[incident.sourceType] || 0) + 1;
    
    // Location
    if (incident.locationId) {
      summary.byLocation[incident.locationId] = (summary.byLocation[incident.locationId] || 0) + 1;
      summary.uniqueLocations.add(incident.locationId);
    }
    
    // Terminal
    if (incident.terminalId) {
      summary.uniqueTerminals.add(incident.terminalId);
    }
    
    // Actor
    if (incident.actor?.userId) {
      summary.uniqueActors.add(incident.actor.userId);
    }
    
    // Time-based
    if (now - incident.occurredAt < day) summary.last24h++;
    if (now - incident.occurredAt < 7 * day) summary.last7d++;
  }
  
  // Convert sets to counts
  summary.locationCount = summary.uniqueLocations.size;
  summary.terminalCount = summary.uniqueTerminals.size;
  summary.actorCount = summary.uniqueActors.size;
  
  delete summary.uniqueLocations;
  delete summary.uniqueTerminals;
  delete summary.uniqueActors;
  
  return summary;
}

/**
 * Update incident with manager action
 * @param {string} incidentId
 * @param {Object} updates
 * @returns {Promise<boolean>}
 */
export async function updateIncident(incidentId, updates) {
  // For now, incidents are derived from queue items
  // Updates go directly to the underlying queue item
  const queueItemId = incidentId.replace('inc_', '');
  
  try {
    const { updateQueueItem } = await import('../offline/db');
    await updateQueueItem(queueItemId, updates);
    invalidateIncidentCache();
    return true;
  } catch (error) {
    console.error('[IncidentStore] Failed to update incident:', error);
    return false;
  }
}

export default {
  aggregateAllIncidents,
  getCachedIncidents,
  invalidateIncidentCache,
  getIncidentSummary,
  updateIncident
};
